import { RadioGroup, RadioButtonGroup, Input, MaskedInput } from '@cian/ui-kit';

import * as React from 'react';
import classNames from 'classnames';

import { ECommercialOwnershipVerificationFormViewFields, EIdentificationPersonType } from '../../../enums';

import { identificationPersonTypeSelectOptions as checkboxGroupOptions } from '../../maps';
import { CADASTRAL_NUMBER_TOOLTIP_TEXT } from '../../constants';

import { FormFooter } from '../FormFooter';
import { CustomFormField, CustomFormFieldContainer } from '../CustomFormField';

import * as styles from './CommercialOwnershipVerificationFormView.css';
import { ICommercialOwnershipVerificationFormViewProps } from './types';

export const CommercialOwnershipVerificationFormView: React.FC<
  ICommercialOwnershipVerificationFormViewProps
> = props => {
  const {
    boundActions,
    checkFormFieldInvalid,
    isDisableFieldsOnPending,
    getFormFieldVisible,
    onBlur,
    getFormFieldErrorMessage,
    getFormFieldValue,
    getFormFieldRequired,
    onSubmit,
    onCancel,
    pending,
    isMobile,
    onInnChange,
  } = props;

  const RadioGroupComponent = isMobile ? RadioButtonGroup : RadioGroup;
  const cadastralNumberFieldWidth = isMobile ? '100%' : 152;

  const identificationPersonTypeSelectOptions = React.useMemo(() => {
    if (!isMobile) {
      return checkboxGroupOptions;
    }

    return checkboxGroupOptions.map(el => {
      if (el.value === EIdentificationPersonType.Natural) {
        return {
          ...el,
          label: 'Физлицо',
        };
      }

      return el;
    });
  }, [isMobile]);

  const companyNameField = (() => {
    const companyName = getFormFieldValue(ECommercialOwnershipVerificationFormViewFields.CompanyName);
    const isCompanyNameVisible =
      !!companyName && getFormFieldVisible(ECommercialOwnershipVerificationFormViewFields.CompanyName);

    return (
      <CustomFormField
        label="Компания"
        isMobile={isMobile}
        containerClassName={styles['form-element']}
        hidden={!isCompanyNameVisible}
      >
        {companyName}
      </CustomFormField>
    );
  })();

  const innField = (() => {
    const innFieldWidth = isMobile ? '100%' : 116;
    const isCompanyPersonType = getFormFieldValue(
      ECommercialOwnershipVerificationFormViewFields.IdentificationPersonType,
    );
    const fieldValueLength = isCompanyPersonType === EIdentificationPersonType.Legal ? 10 : 12;
    const placeholder = '0'.repeat(fieldValueLength);
    const mask = '9'.repeat(fieldValueLength);

    return (
      <CustomFormFieldContainer
        name={ECommercialOwnershipVerificationFormViewFields.Inn}
        isMobile={isMobile}
        containerClassName={styles['form-element']}
        getFormFieldRequired={getFormFieldRequired}
        getFormFieldVisible={getFormFieldVisible}
        getFormFieldErrorMessage={getFormFieldErrorMessage}
      >
        <MaskedInput
          mask={mask}
          placeholder={placeholder}
          width={innFieldWidth}
          invalid={checkFormFieldInvalid(ECommercialOwnershipVerificationFormViewFields.Inn)}
          disabled={isDisableFieldsOnPending && pending}
          onBlur={onBlur(ECommercialOwnershipVerificationFormViewFields.Inn)}
          onChange={onInnChange}
          value={getFormFieldValue(ECommercialOwnershipVerificationFormViewFields.Inn)}
        />
      </CustomFormFieldContainer>
    );
  })();

  return (
    <form className={classNames(styles['form-container'], { [styles['mobile']]: isMobile })} onSubmit={onSubmit}>
      <div className={styles['form-group']}>
        <CustomFormFieldContainer
          name={ECommercialOwnershipVerificationFormViewFields.CadastralNumber}
          isMobile={isMobile}
          containerClassName={styles['form-element']}
          getFormFieldRequired={getFormFieldRequired}
          getFormFieldVisible={getFormFieldVisible}
          getFormFieldErrorMessage={getFormFieldErrorMessage}
          tooltipContent={CADASTRAL_NUMBER_TOOLTIP_TEXT}
        >
          <Input
            placeholder="00:00:0000000:0000"
            width={cadastralNumberFieldWidth}
            invalid={checkFormFieldInvalid(ECommercialOwnershipVerificationFormViewFields.CadastralNumber)}
            disabled={isDisableFieldsOnPending && pending}
            onBlur={onBlur(ECommercialOwnershipVerificationFormViewFields.CadastralNumber)}
            onChange={boundActions.setCadastralNumberFieldValueAction}
            value={getFormFieldValue(ECommercialOwnershipVerificationFormViewFields.CadastralNumber)}
          />
        </CustomFormFieldContainer>

        <CustomFormField label="Кто собственник?" isMobile={isMobile} containerClassName={styles['form-element']}>
          <RadioGroupComponent
            horizontal
            disabled={isDisableFieldsOnPending && pending}
            invalid={checkFormFieldInvalid(ECommercialOwnershipVerificationFormViewFields.IdentificationPersonType)}
            onChange={boundActions.setIdentificationPersonTypeFieldValueAction}
            options={identificationPersonTypeSelectOptions}
            value={getFormFieldValue(ECommercialOwnershipVerificationFormViewFields.IdentificationPersonType)}
          />
        </CustomFormField>

        {innField}

        {companyNameField}

        <CustomFormFieldContainer
          fullWidth
          name={ECommercialOwnershipVerificationFormViewFields.LastName}
          isMobile={isMobile}
          containerClassName={styles['form-element']}
          getFormFieldRequired={getFormFieldRequired}
          getFormFieldVisible={getFormFieldVisible}
          getFormFieldErrorMessage={getFormFieldErrorMessage}
        >
          <Input
            invalid={checkFormFieldInvalid(ECommercialOwnershipVerificationFormViewFields.LastName)}
            disabled={isDisableFieldsOnPending && pending}
            onBlur={onBlur(ECommercialOwnershipVerificationFormViewFields.LastName)}
            onChange={boundActions.setLastNameFieldValueAction}
            value={getFormFieldValue(ECommercialOwnershipVerificationFormViewFields.LastName)}
          />
        </CustomFormFieldContainer>

        <CustomFormFieldContainer
          fullWidth
          name={ECommercialOwnershipVerificationFormViewFields.FirstName}
          isMobile={isMobile}
          containerClassName={styles['form-element']}
          getFormFieldRequired={getFormFieldRequired}
          getFormFieldVisible={getFormFieldVisible}
          getFormFieldErrorMessage={getFormFieldErrorMessage}
        >
          <Input
            invalid={checkFormFieldInvalid(ECommercialOwnershipVerificationFormViewFields.FirstName)}
            disabled={isDisableFieldsOnPending && pending}
            onBlur={onBlur(ECommercialOwnershipVerificationFormViewFields.FirstName)}
            onChange={boundActions.setFirstNameFieldValueAction}
            value={getFormFieldValue(ECommercialOwnershipVerificationFormViewFields.FirstName)}
          />
        </CustomFormFieldContainer>

        <CustomFormFieldContainer
          fullWidth
          name={ECommercialOwnershipVerificationFormViewFields.SecondName}
          isMobile={isMobile}
          containerClassName={styles['form-element']}
          getFormFieldRequired={getFormFieldRequired}
          getFormFieldVisible={getFormFieldVisible}
          getFormFieldErrorMessage={getFormFieldErrorMessage}
        >
          <Input
            invalid={checkFormFieldInvalid(ECommercialOwnershipVerificationFormViewFields.SecondName)}
            disabled={isDisableFieldsOnPending && pending}
            onBlur={onBlur(ECommercialOwnershipVerificationFormViewFields.SecondName)}
            onChange={boundActions.setSecondNameFieldValueAction}
            value={getFormFieldValue(ECommercialOwnershipVerificationFormViewFields.SecondName)}
          />
        </CustomFormFieldContainer>
      </div>

      <FormFooter isMobile={isMobile} className={styles['form-footer']} disabled={pending} onCancel={onCancel} />
    </form>
  );
};

CommercialOwnershipVerificationFormView.displayName = 'CommercialOwnershipVerificationFormView';
